import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { etudiantsAPI } from '../services/api';
import PhotoEtudiant, { viderCachePhotos } from './PhotoEtudiant';

/**
 * ONGLET 3 — PHOTOS SCODOC
 * ========================
 * Spec : doc/modules/etudiants.md §Onglet 3 · maquette `etudiants_maquette_v5.html`
 * Lot 1.5. Migration v40.
 *
 * ── LA DISPOSITION VIENT DE LA MAQUETTE, ET ELLE DIT UNE PRIORITÉ ────────────
 * Le trombinoscope occupe le haut et toute la largeur ; l'import et l'historique
 * sont relégués dans une barre basse. Ce n'est pas un détail de mise en page :
 * l'écran sert à REGARDER des visages, pas à importer des archives. L'import est
 * un geste de rentrée, la consultation un geste quotidien.
 *
 * ── ⚠️ CE COMPOSANT NE CHARGE PAS LA LISTE ──────────────────────────────────
 * Il la reçoit. `Etudiants.js` la détient déjà pour l'onglet 1 et la recharge
 * quand l'onglet change ; en redemander une copie ici aurait fait deux lectures
 * du même périmètre, et deux comptes qui finissent par diverger.
 *
 * C'est aussi ce qui fait que « la grille suit les filtres de l'onglet 1 »
 * (spec) sans une ligne de code : les filtres sont dans l'état du parent.
 *
 * ── ⚠️ ET C'EST POURQUOI LE FILTRE ACTIF EST AFFICHÉ ICI ────────────────────
 * La barre de filtres vit DANS l'onglet 1 : elle n'est pas visible sur celui-ci.
 * Une grille filtrée sans dire par quoi se lit comme une grille incomplète — on
 * chercherait des étudiants absents. L'en-tête nomme donc l'année et la
 * formation regardées, et le dit quand un autre filtre restreint encore.
 * (Écart E47.)
 */

/** Les motifs qui ne sont PAS des erreurs, seulement des faits à connaître.
 *  Même règle que les deux autres imports : orange, jamais rouge. */
// ⚠️ `etudiant_desactive` en fait partie : l archive est bonne, le numero est
// connu — c est la REGLE DE CONSERVATION qui refuse, pas le fichier qui est mauvais.
// Le peindre en rouge ferait chercher un defaut dans l archive.
const MOTIFS_AVERTISSEMENT = ['non_trouve', 'hors_annee', 'etudiant_desactive'];

const dateHeure = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return d.toLocaleDateString('fr-FR') + ' à ' + d.toLocaleTimeString('fr-FR', {
        hour: '2-digit', minute: '2-digit'
    });
};

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-FR') : '');

/** Le nom de famille seul sous la vignette (maquette) : « LASSOUED Lyna » ne tient
 *  pas dans 62 px, et le prénom n'est pas ce qui permet de reconnaître un visage
 *  dans une liste de classe. L'infobulle porte le nom entier. */
const nomCourt = (l) => (l.nom || '').trim() || (l.prenom || '').trim() || '—';

function PhotosScodoc({
    // `filtresActifs` n est PAS repris : cet ecran n a pas de bouton « retirer les
    // filtres » — ils appartiennent a l onglet 1, qui seul peut les changer. Il en
    // NOMME l effet, ce qui est different.
    lignes, chargement, erreur, annee, formations, filtres,
    departementId, anneeId, onImporte
}) {
    const [envoi, setEnvoi] = useState(false);
    // 2026-10-22 : l'avancement de l'import en tâche de fond — { traitees, total } (total inconnu au tout début).
    const [progres, setProgres] = useState(null);
    const [bilan, setBilan] = useState(null);
    const [refus, setRefus] = useState(null);
    const [sansPhotoSeulement, setSansPhotoSeulement] = useState(false);
    const [historique, setHistorique] = useState([]);

    // ── Les comptes de l'en-tête ─────────────────────────────────────────────
    const { avecPhoto, total, manquants } = useMemo(() => {
        const l = lignes || [];
        const n = l.filter((e) => !!e.photo_url).length;
        return { avecPhoto: n, total: l.length, manquants: l.length - n };
    }, [lignes]);

    // Le filtre « sans photo » n'a plus d'objet s'il ne reste plus de manquant :
    // le laisser actif afficherait une grille vide sans expliquer pourquoi.
    useEffect(() => {
        if (sansPhotoSeulement && manquants === 0) setSansPhotoSeulement(false);
    }, [manquants, sansPhotoSeulement]);

    const grille = useMemo(() => {
        const l = lignes || [];
        return sansPhotoSeulement ? l.filter((e) => !e.photo_url) : l;
    }, [lignes, sansPhotoSeulement]);

    // ── L'historique, par la route générique (écart E4) ──────────────────────
    const chargerHistorique = useCallback(async () => {
        if (!departementId) return;
        try {
            const r = await etudiantsAPI.getImports(departementId, 'photos');
            setHistorique(r.data?.imports || []);
        } catch (e) {
            // L'historique est un confort : son absence ne doit pas masquer le
            // trombinoscope, qui est la raison d'être de l'écran.
            setHistorique([]);
        }
    }, [departementId]);

    useEffect(() => { chargerHistorique(); }, [chargerHistorique]);

    // ── L'import ─────────────────────────────────────────────────────────────
    const envoyer = async (evenement) => {
        const fichier = evenement.target.files?.[0];
        // Le champ est vidé TOUT DE SUITE : sans cela, réimporter le même fichier
        // après une correction ne déclenche aucun `change` et l'écran paraît figé.
        evenement.target.value = '';
        if (!fichier) return;

        setEnvoi(true);
        setBilan(null);
        setRefus(null);
        setProgres(null);
        try {
            /**
             * ⚠️ 2026-10-22 (504 en production) : l'archive part, le serveur rend AUSSITÔT une tâche ; on suit son
             * avancement jusqu'au bilan. Plus de requête de plusieurs minutes que le proxy du NAS couperait.
             */
            const depart = await etudiantsAPI.importerPhotos(departementId, anneeId, fichier);
            let etat = null;
            for (;;) {
                await new Promise((ok) => setTimeout(ok, 1500));
                etat = (await etudiantsAPI.etatImportPhotos(departementId, depart.data.tache_id)).data;
                setProgres({ traitees: etat.traitees || 0, total: etat.total });
                if (etat.etat === 'fini') break;
            }
            if (etat.erreur) throw Object.assign(new Error(etat.erreur), { response: { data: { error: etat.erreur } } });
            if (etat.refus) throw Object.assign(new Error(etat.refus.error), { response: { data: etat.refus } });
            setBilan({ annee: depart.data.annee, ...etat.bilan });
            // ⚠️ INDISPENSABLE. `photo_url` ne change PAS quand une photo est
            // remplacée — c'est la même route — donc le cache servirait l'ancienne
            // image et l'import paraîtrait sans effet.
            viderCachePhotos();
            await chargerHistorique();
            // La liste du parent porte `photo_url` : sans ce rappel, les vignettes
            // qu'on vient d'importer resteraient grises jusqu'au prochain
            // changement d'onglet.
            if (onImporte) onImporte();
        } catch (e) {
            const d = e.response?.data || {};
            setRefus({
                error: d.error || 'L\'import a échoué',
                detail: d.detail || null,
                anomalies: Array.isArray(d.anomalies) ? d.anomalies : []
            });
        } finally {
            setEnvoi(false);
            setProgres(null);
        }
    };

    // ── Ce que la grille montre, et de qui ───────────────────────────────────
    const formationNom = useMemo(() => {
        if (!filtres?.sousNiveauId) return null;
        const f = (formations || []).find((x) => String(x.id) === String(filtres.sousNiveauId));
        return f ? f.nom : null;
    }, [filtres, formations]);

    const autresFiltres = !!(filtres?.regime || filtres?.statut || filtres?.boursier
                          || filtres?.recherche);

    return (
        <div className="etu-photos">

            {/* ═══ LE TROMBINOSCOPE — priorité principale, pleine largeur ═══ */}
            <div className="etu-carte etu-carte-trombi">
                <div className="etu-carte-entete">
                    <i className="fa-solid fa-id-card etu-icone-trombi" />
                    Trombinoscope
                    <span className="etu-chip-compte">{avecPhoto} / {total}</span>
                    <span className="etu-trombi-soustitre">étudiants avec photo</span>

                    {/* Le périmètre regardé, écrit — voir l'en-tête du fichier. */}
                    <span className="etu-trombi-perimetre">
                        <i className="fa-solid fa-filter" />
                        {annee?.libelle || 'année en cours'}
                        {formationNom ? ' · ' + formationNom : ' · toutes formations'}
                        {autresFiltres && ' · filtres de l’onglet Étudiants actifs'}
                    </span>

                    {/* « n sans photo » filtre la grille (spec). Un bouton et non un
                        texte cliquable : il change l'état de l'écran, et il doit
                        être atteignable au clavier. */}
                    {manquants > 0 && (
                        <button
                            type="button"
                            className={'etu-trombi-manquants' + (sansPhotoSeulement ? ' actif' : '')}
                            onClick={() => setSansPhotoSeulement((v) => !v)}
                            title={sansPhotoSeulement
                                ? 'Revenir à tous les étudiants'
                                : 'Ne montrer que les étudiants sans photo'}
                        >
                            <i className={'fa-solid ' + (sansPhotoSeulement ? 'fa-users' : 'fa-user-slash')} />
                            {sansPhotoSeulement ? 'Tous les étudiants' : manquants + ' sans photo'}
                        </button>
                    )}
                </div>

                <div className="etu-trombi-cadre">
                    {chargement && (
                        <div className="etu-vide">
                            <i className="fa-solid fa-circle-notch etu-tourne" />
                            <div className="etu-vide-titre">Lecture des étudiants…</div>
                        </div>
                    )}

                    {!chargement && erreur && (
                        <div className="etu-vide">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <div className="etu-vide-titre">{erreur}</div>
                        </div>
                    )}

                    {!chargement && !erreur && grille.length === 0 && (
                        <div className="etu-vide">
                            <i className="fa-solid fa-camera" />
                            <div className="etu-vide-titre">
                                {total === 0
                                    ? 'Aucun étudiant pour ce périmètre'
                                    : 'Tous les étudiants affichés ont leur photo'}
                            </div>
                            <p className="etu-vide-texte">
                                {total === 0
                                    ? 'Les photos se rattachent par numéro étudiant : importe d’abord '
                                      + 'les étudiants dans l’onglet Imports.'
                                    : 'Rien à compléter ici.'}
                            </p>
                        </div>
                    )}

                    {!chargement && !erreur && grille.length > 0 && (
                        <div className="etu-trombi-grille">
                            {grille.map((e) => (
                                <div className="etu-trombi-tuile" key={e.id}>
                                    {/* ⚠️ PAS un <img src={photo_url}> : cette route
                                        exige le jeton, que le navigateur n'envoie pas
                                        de lui-même. Voir PhotoEtudiant.js. */}
                                    <PhotoEtudiant
                                        chemin={e.photo_url}
                                        className="etu-trombi-photo"
                                        alt={`${e.nom} ${e.prenom}`}
                                        title={`${e.nom} ${e.prenom} — n° ${e.code_etudiant || '—'}`}
                                        secours={(
                                            <div
                                                className="etu-trombi-vide"
                                                title={`${e.nom} ${e.prenom} — n° ${e.code_etudiant || '—'} `
                                                     + '— aucune photo'}
                                            >
                                                <i className="fa-solid fa-user-slash" />
                                            </div>
                                        )}
                                    />
                                    <div
                                        className="etu-trombi-nom"
                                        title={`${e.nom} ${e.prenom}`}
                                    >
                                        {nomCourt(e)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ═══ LA BARRE BASSE : import + résultat | historique ═══ */}
            <div className="etu-photos-bas">

                <div className="etu-photos-import">

                    {/* Zone de dépôt COMPACTE et horizontale (maquette) —
                        délibérément différente des grandes cartes APOGEE et SCODOC :
                        ici l'import n'est pas le sujet de l'écran. */}
                    <div className="etu-photos-depot">
                        <i className="fa-solid fa-file-zipper etu-photos-depot-icone" />
                        <div className="etu-photos-depot-texte">
                            <div className="etu-photos-depot-titre">
                                Photos SCODOC — fichier <code>.zip</code>
                            </div>
                            <div className="etu-photos-depot-detail">
                                Photos nommées <code>20253975.jpg</code> — les étudiants doivent
                                être importés en premier
                            </div>
                        </div>
                        {/* Le bouton est un <label> qui PORTE l'input, comme les deux
                            autres imports : l'input reste invisible mais focalisable
                            au clavier, ce qu'un `display: none` lui retirerait. */}
                        <label
                            className={'etu-btn etu-btn-photos'
                                + (envoi || !departementId ? ' etu-btn-inerte' : '')}
                            title={envoi ? 'Import en cours…' : 'Choisir une archive .zip'}
                        >
                            {envoi
                                ? <><i className="fa-solid fa-circle-notch etu-tourne" /> {progres?.total
                                    ? `Traitement des photos… ${progres.traitees} / ${progres.total}`
                                    : 'Envoi de l’archive…'}</>
                                : <><i className="fa-solid fa-file-zipper" /> Choisir .zip</>}
                            <input
                                type="file"
                                accept=".zip,application/zip"
                                disabled={envoi || !departementId}
                                onChange={envoyer}
                            />
                        </label>
                    </div>

                    {/* La garantie, comme sur les deux autres imports : ce que cet
                        import ne fera jamais. */}
                    <div className="etu-alerte etu-alerte-garantie">
                        <i className="fa-solid fa-shield-halved" />
                        <span>
                            Cet import ne modifie <strong>que les photos</strong>. Il ne crée aucun
                            étudiant : un numéro que la base ne connaît pas est compté « non trouvé »
                            et sa photo <strong>n’est pas conservée</strong>. Les images sont
                            stockées <strong>chiffrées</strong>, et jamais diffusées vers les
                            portails étudiant ou entreprise.
                        </span>
                    </div>

                    {refus && (
                        <div className="etu-bilan">
                            <div className="etu-bilan-entete etu-bilan-photos avec-erreurs">
                                <i className="fa-solid fa-circle-exclamation" />
                                <span>{refus.error}</span>
                            </div>
                            {refus.detail && (
                                <div className="etu-photos-refus-detail">{refus.detail}</div>
                            )}
                            {refus.anomalies.length > 0 && (
                                <ListeAnomalies anomalies={refus.anomalies} />
                            )}
                        </div>
                    )}

                    {bilan && !refus && <Bilan bilan={bilan} />}

                    {!bilan && !refus && historique.length > 0 && (
                        <Bilan bilan={bilanDepuisJournal(historique[0])} dernier />
                    )}
                </div>

                {/* ── L'historique, 280 px à droite (maquette) ── */}
                <div className="etu-photos-histo">
                    <div className="etu-carte">
                        <div className="etu-carte-entete">
                            <i className="fa-solid fa-clock-rotate-left" />
                            Historique imports photos
                        </div>
                        {historique.length === 0 ? (
                            <div className="etu-histo-vide">Aucun import de photos</div>
                        ) : (
                            <div className="etu-table-cadre">
                                <table className="etu-table etu-table-histo">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Fichier</th>
                                            {/* ⚠️ DEUX colonnes chiffrées, comme la maquette,
                                                alors que le bilan en montre trois. Le ✅ réunit
                                                associées ET remplacées — tout ce qui a produit
                                                une photo ; le ❌ réunit non trouvées et erreurs
                                                — tout ce qui n'en a pas produit. C'est la seule
                                                lecture où les deux colonnes s'additionnent pour
                                                donner le nombre de fichiers traités. */}
                                            <th className="etu-histo-ok"
                                                title="Photos ÉCRITES (associées + remplacées). Une photo identique à celle déjà en base n’est pas réécrite et ne compte pas ici — survole une ligne pour le détail.">✅</th>
                                            <th className="etu-histo-ko" title="Fichiers sans effet (non trouvés + erreurs)">❌</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historique.map((h) => (
                                            <tr
                                                key={h.id}
                                                /* ⚠️ Le détail EN INFOBULLE, parce que la
                                                   carte fait 280 px et ne tient pas quatre
                                                   colonnes chiffrées. Sans lui, deux imports
                                                   aux effets opposés se lisent pareil. */
                                                title={`${h.nb_crees || 0} associée(s), `
                                                     + `${h.nb_mis_a_jour || 0} remplacée(s), `
                                                     + `${h.nb_ignores || 0} inchangée(s), `
                                                     + `${h.nb_non_trouves || 0} non trouvée(s), `
                                                     + `${h.nb_erreurs || 0} erreur(s)`}
                                            >
                                                <td>{dateCourte(h.created_at)}</td>
                                                <td className="etu-histo-fichier" title={h.fichier_nom || ''}>
                                                    <i className="fa-solid fa-file-zipper" />
                                                    {h.fichier_nom || '—'}
                                                </td>
                                                <td className="etu-histo-ok">
                                                    {(h.nb_crees || 0) + (h.nb_mis_a_jour || 0)}
                                                </td>
                                                <td className="etu-histo-ko">
                                                    {(h.nb_non_trouves || 0) + (h.nb_erreurs || 0)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Les anomalies, nommées PAR FICHIER (spec) — il n'y a pas de numéro de ligne
 * dans une archive, et en inventer un obligerait à ouvrir le zip pour comprendre.
 */
function ListeAnomalies({ anomalies }) {
    const bloquantes = anomalies.filter((a) => !MOTIFS_AVERTISSEMENT.includes(a.motif));
    const avertissements = anomalies.filter((a) => MOTIFS_AVERTISSEMENT.includes(a.motif));

    if (!bloquantes.length && !avertissements.length) return null;

    return (
        <div className="etu-bilan-anomalies">
            {bloquantes.map((a, i) => (
                <div className="etu-detail-ligne etu-detail-refus" key={'b' + i}>
                    <i className="fa-solid fa-triangle-exclamation" />
                    <span className="etu-detail-num">{a.fichier}</span>
                    <span>{a.message}</span>
                </div>
            ))}
            {avertissements.map((a, i) => (
                <div className="etu-detail-ligne etu-detail-avert" key={'w' + i}>
                    <i className={'fa-solid '
                        + (a.motif === 'hors_annee' ? 'fa-calendar-xmark'
                           : a.motif === 'etudiant_desactive' ? 'fa-user-slash'
                           : 'fa-circle-question')} />
                    <span className="etu-detail-num">{a.fichier}</span>
                    <span>{a.message}</span>
                </div>
            ))}
        </div>
    );
}

/**
 * TROIS compteurs, au vocabulaire propre à cet onglet (spec) : Associées,
 * Remplacées, Non trouvées. Un quatrième pour les fichiers inexploitables — les
 * confondre avec les « non trouvées » empêcherait de savoir quoi corriger : un
 * numéro inconnu s'explique par un import APOGEE en retard, un nom de fichier
 * invalide par une archive mal préparée.
 */
function Bilan({ bilan, dernier }) {
    const anomalies = Array.isArray(bilan.anomalies) ? bilan.anomalies : [];
    const enErreur = (bilan.nb_erreurs || 0) > 0;

    return (
        <div className="etu-bilan">
            <div className={'etu-bilan-entete etu-bilan-photos' + (enErreur ? ' avec-erreurs' : '')}>
                <i className={'fa-solid ' + (enErreur ? 'fa-circle-exclamation' : 'fa-circle-check')} />
                <span>
                    {dernier && <span className="etu-bilan-dernier">Dernier import — </span>}
                    {dateHeure(bilan.date_import)}
                    {bilan.fichier_nom ? ' — ' + bilan.fichier_nom : ''}
                    {bilan.nb_fichiers ? ` (${bilan.nb_fichiers} fichiers)` : ''}
                </span>
            </div>

            <div className="etu-compteurs">
                <div className="etu-compteur">
                    <div className="etu-compteur-nb etu-nb-crees">{bilan.nb_associees || 0}</div>
                    <div className="etu-compteur-label">Associées</div>
                </div>
                <div className="etu-compteur">
                    <div className="etu-compteur-nb etu-nb-majs">{bilan.nb_remplacees || 0}</div>
                    <div className="etu-compteur-label">Remplacées</div>
                </div>
                {/* ⚠️ « INCHANGÉES » — ajouté le 2026-09-11, sur un défaut signalé par
                    Bertrand. Une photo identique à celle déjà en base n'est plus
                    réécrite. Sans ce compteur, réimporter l'archive entière pour
                    rattraper UNE photo manquante affichait « 68 remplacées » — un
                    chiffre vrai et parfaitement trompeur, indiscernable d'un premier
                    import. Les deux autres imports du module ont ce compteur depuis
                    le lot 1.2 ; il manquait ici. */}
                <div className="etu-compteur">
                    <div className="etu-compteur-nb etu-nb-identiques">{bilan.nb_inchangees || 0}</div>
                    <div className="etu-compteur-label">Inchangées</div>
                </div>
                <div className="etu-compteur">
                    <div className="etu-compteur-nb etu-nb-non-trouves">{bilan.nb_non_trouvees || 0}</div>
                    <div className="etu-compteur-label">Non trouvées</div>
                </div>
                <div className="etu-compteur">
                    <div className="etu-compteur-nb etu-nb-erreurs">{bilan.nb_erreurs || 0}</div>
                    <div className="etu-compteur-label">Erreurs</div>
                </div>
            </div>

            <ListeAnomalies anomalies={anomalies} />
        </div>
    );
}

/** Une ligne du journal relue comme un bilan — `detail_erreurs` est déjà en base,
 *  et les noms de colonnes d'APOGEE sont retraduits dans le vocabulaire d'ici. */
function bilanDepuisJournal(ligne) {
    return {
        import_id: ligne.id,
        date_import: ligne.created_at,
        fichier_nom: ligne.fichier_nom,
        nb_associees: ligne.nb_crees,
        nb_remplacees: ligne.nb_mis_a_jour,
        // `nb_ignores` porte les INCHANGÉES pour cet import — la colonne existe
        // depuis la v38 et les deux autres imports s'en servent pour la même idée.
        nb_inchangees: ligne.nb_ignores,
        nb_non_trouvees: ligne.nb_non_trouves,
        nb_erreurs: ligne.nb_erreurs,
        anomalies: Array.isArray(ligne.detail_erreurs) ? ligne.detail_erreurs : []
    };
}

export default PhotosScodoc;
